import { tournamentPath } from "./tournamentUrls";

export const formatSquadListDate = (dateString) => {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("-").map(Number);
  const date = new Date(year, month - 1, day, 12, 0, 0);
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

export const scoringHubSquadPath = (tournamentId, squadId) =>
  `${tournamentPath(tournamentId, "/scoring")}?squad=${squadId}`;

const sortSquads = (items) =>
  [...(items || [])].sort((left, right) => {
    const dateCmp = String(left.squadDate || "").localeCompare(
      String(right.squadDate || "")
    );
    if (dateCmp !== 0) return dateCmp;
    const timeCmp = String(left.squadTime || "").localeCompare(
      String(right.squadTime || "")
    );
    if (timeCmp !== 0) return timeCmp;
    return String(left.tournamentEvents?.eventName || "").localeCompare(
      String(right.tournamentEvents?.eventName || "")
    );
  });

export const groupSquadsByDateAndHouse = (items) => {
  const groups = [];
  const byKey = new Map();
  sortSquads(items).forEach((squad) => {
    const houseId =
      squad.bowlingCenters?.id || squad.squadLocation || "no-house";
    const key = `${squad.squadDate || ""}|${houseId}`;
    let group = byKey.get(key);
    if (!group) {
      group = {
        key,
        date: squad.squadDate,
        houseName: squad.bowlingCenters?.centerName || "",
        houseAddress: squad.bowlingCenters?.formattedAddress || "",
        sessions: [],
        sessionMap: new Map(),
      };
      byKey.set(key, group);
      groups.push(group);
    }
    const timeKey = squad.squadTime || "";
    let session = group.sessionMap.get(timeKey);
    if (!session) {
      session = { time: timeKey, squads: [] };
      group.sessionMap.set(timeKey, session);
      group.sessions.push(session);
    }
    session.squads.push(squad);
  });
  return groups.map(({ sessionMap, ...group }) => group);
};

const parseIsoDate = (iso) => {
  const [year, month, day] = String(iso).split("-").map(Number);
  return new Date(year, month - 1, day, 12, 0, 0);
};

const isoFromDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const consecutiveDateGroups = (dates) => {
  const unique = [...new Set((dates || []).filter(Boolean))].sort();
  const groups = [];
  unique.forEach((day) => {
    const last = groups[groups.length - 1];
    if (!last) {
      groups.push([day]);
      return;
    }
    const next = parseIsoDate(last[last.length - 1]);
    next.setDate(next.getDate() + 1);
    if (isoFromDate(next) === day) last.push(day);
    else groups.push([day]);
  });
  return groups;
};

const formatRangeGroup = (group) => {
  if (group.length === 1) return formatSquadListDate(group[0]);
  const start = parseIsoDate(group[0]);
  const end = parseIsoDate(group[group.length - 1]);
  const startMonth = start.toLocaleDateString("en-US", { month: "short" });
  const endMonth = end.toLocaleDateString("en-US", { month: "short" });
  if (startMonth === endMonth) {
    return `${startMonth} ${start.getDate()}–${end.getDate()}`;
  }
  return `${startMonth} ${start.getDate()}–${endMonth} ${end.getDate()}`;
};

export const formatSquadDateRange = (dates) => {
  const unique = [...new Set((dates || []).filter(Boolean))].sort();
  if (unique.length === 0) return "";
  if (unique.length === 1) {
    const date = parseIsoDate(unique[0]);
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }
  const year = parseIsoDate(unique[unique.length - 1]).getFullYear();
  return `${consecutiveDateGroups(unique).map(formatRangeGroup).join(" and ")}, ${year}`;
};

export const formatHeaderHouses = (centers) => {
  const houses = (centers || []).filter(Boolean);
  if (houses.length === 0) return "";
  if (houses.length === 1) {
    const house = houses[0];
    const city = [house.city, house.stateShort || house.state]
      .filter(Boolean)
      .join(", ");
    if (house.centerName && city) return `${house.centerName} · ${city}`;
    return house.centerName || city;
  }
  return houses
    .map((house) => house.city || house.centerName)
    .filter(Boolean)
    .join(" · ");
};

export const groupSquadsByDate = (items) => {
  const days = [];
  groupSquadsByDateAndHouse(items).forEach((house) => {
    const last = days[days.length - 1];
    if (last && last.date === house.date) {
      last.houses.push(house);
    } else {
      days.push({
        key: house.date || "no-date",
        date: house.date,
        houses: [house],
      });
    }
  });
  return days;
};
